import { useEffect, useState } from "react";
import api from "../services/api";
import { Package, CheckCircle2, Clock, Users, IndianRupee, Star, TrendingUp, Truck, Loader2, BarChart3 } from "lucide-react";

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/analytics/overview")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
    </div>
  );

  const statCards = [
    { label: "Total Orders", value: data?.total_orders ?? 0, icon: Package, color: "text-[#FF6B00]", bg: "bg-orange-50" },
    { label: "Delivered", value: data?.delivered ?? 0, icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50" },
    { label: "In Progress", value: data?.in_progress ?? 0, icon: Truck, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Pending", value: data?.pending ?? 0, icon: Clock, color: "text-slate-600", bg: "bg-slate-100" },
    { label: "Total Revenue", value: `INR ${data?.total_revenue ?? 0}`, icon: IndianRupee, color: "text-green-600", bg: "bg-green-50" },
    { label: "Avg Rating", value: data?.avg_rating ? `${data.avg_rating}/5` : "N/A", icon: Star, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Total Agents", value: data?.total_agents ?? 0, icon: Users, color: "text-violet-600", bg: "bg-violet-50" },
    { label: "Agents Online", value: data?.online_agents ?? 0, icon: Users, color: "text-teal-600", bg: "bg-teal-50" },
    { label: "Total Users", value: data?.total_users ?? 0, icon: TrendingUp, color: "text-blue-600", bg: "bg-blue-50" },
  ];

  const statusData = data?.by_status || {};
  const statusTotal = Object.values(statusData).reduce((a, b) => a + b, 0) || 1;

  const statusBars = [
    { key: "pending", label: "Pending", color: "bg-slate-400" },
    { key: "assigned", label: "Assigned", color: "bg-blue-500" },
    { key: "picked_up", label: "Picked Up", color: "bg-[#FF6B00]" },
    { key: "in_transit", label: "In Transit", color: "bg-orange-400" },
    { key: "delivered", label: "Delivered", color: "bg-green-500" },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="animate-fadeInUp">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Platform Analytics</h1>
        <p className="text-sm text-slate-500 mt-1">Comprehensive overview of your logistics platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {statCards.map((card, i) => (
          <div key={card.label} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 card-hover animate-fadeInUp" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`analytics-${card.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 ${card.bg} rounded-xl flex items-center justify-center`}>
                <card.icon className={`h-5 w-5 ${card.color}`} />
              </div>
              <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 live-dot" /> Live
              </span>
            </div>
            <p className="font-['Outfit'] text-2xl font-bold text-slate-900 tracking-tight">{card.value}</p>
            <p className="text-sm text-slate-500 mt-0.5">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Order Status Breakdown */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="analytics-status-breakdown">
        <div className="flex items-center gap-2 mb-5">
          <BarChart3 className="h-5 w-5 text-[#FF6B00]" />
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Order Status Breakdown</h2>
        </div>
        <div className="space-y-4">
          {statusBars.map((s) => {
            const count = statusData[s.key] || 0;
            const pct = Math.round((count / statusTotal) * 100);
            return (
              <div key={s.key} data-testid={`status-bar-${s.key}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-slate-700">{s.label}</span>
                  <span className="text-sm font-bold text-slate-900">{count} <span className="text-xs font-normal text-slate-400">({pct}%)</span></span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${s.color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Daily Orders Chart */}
      {data?.daily_orders?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="analytics-daily-chart">
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900 mb-5">Orders This Week</h2>
          <div className="flex items-end gap-3 h-40">
            {data.daily_orders.map((d) => {
              const maxCount = Math.max(...data.daily_orders.map((x) => x.count));
              const height = maxCount > 0 ? (d.count / maxCount) * 100 : 10;
              return (
                <div key={d._id} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-bold text-slate-700">{d.count}</span>
                  <div className="w-full bg-[#FF6B00] rounded-t-lg transition-all duration-500" style={{ height: `${Math.max(height, 8)}%` }} />
                  <span className="text-[10px] text-slate-400 font-medium">{d._id.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Ratings Summary */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="analytics-ratings">
        <div className="flex items-center gap-2 mb-3">
          <Star className="h-5 w-5 text-amber-500" />
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Customer Ratings</h2>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="font-['Outfit'] text-4xl font-bold text-slate-900">{data?.avg_rating || "N/A"}</p>
            <div className="flex items-center gap-0.5 justify-center mt-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className={`h-4 w-4 ${s <= Math.round(data?.avg_rating || 0) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1">{data?.total_ratings || 0} reviews</p>
          </div>
        </div>
      </div>
    </div>
  );
}
